import { addDays, differenceInCalendarDays, format, parseISO } from "date-fns";
import {
  getExtractTransactionAmountCents,
  isExtractCreditTransaction,
} from "../../../Pages/Private/Extract/extractFilters";
import { PIX_RECEIVE_PRODUCT_ID } from "../../../Pages/Private/Pix/pixStatementFilters";
import { isStatementRefund } from "../../../Utils/buildReceiptFromStatementItem";

export const PIX_PAYMENT_PRODUCT_ID = "149";

const normalizeText = (value) =>
  String(value ?? "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

const parseDayKey = (value) => {
  if (!value) return null;
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return format(value, "yyyy-MM-dd");
  }
  const raw = String(value).trim();
  if (!raw) return null;
  try {
    if (/^\d{4}-\d{2}-\d{2}/.test(raw)) {
      return format(parseISO(raw.slice(0, 10)), "yyyy-MM-dd");
    }
    const asDate = new Date(raw);
    if (!Number.isNaN(asDate.getTime())) {
      return format(asDate, "yyyy-MM-dd");
    }
  } catch {
    return null;
  }
  return null;
};

export const isPixStatementItem = (item) => {
  if (!item) return false;

  const explicitId = item?.product_id ?? item?.productId ?? item?.category?.id ?? item?.category_id;
  if (explicitId != null && explicitId !== "") {
    const id = String(explicitId);
    if (id === PIX_RECEIVE_PRODUCT_ID || id === PIX_PAYMENT_PRODUCT_ID || id === "6") {
      return true;
    }
  }

  const haystack = normalizeText(
    [item?.description, item?.name, item?.category?.name, item?.operation]
      .filter(Boolean)
      .join(" "),
  );

  return /\bpix\b/.test(haystack);
};

/** MD06 / titularidade / estorno — cash left the account (saída), even if tagged as receive. */
export const isPixChargebackOrReturnItem = (item) => isStatementRefund(item);

const addPixInOutCents = (item, bucket) => {
  const cents = getExtractTransactionAmountCents(item);
  if (isPixChargebackOrReturnItem(item) || !isExtractCreditTransaction(item)) {
    bucket.saidaCents += cents;
  } else {
    bucket.entradaCents += cents;
  }
};

export const resolvePixInOutFromItems = (items = []) => {
  const totals = { entradaCents: 0, saidaCents: 0 };

  (items || []).forEach((item) => {
    if (!isPixStatementItem(item)) return;
    addPixInOutCents(item, totals);
  });

  return totals;
};

export const buildDailyPixSeries = (
  items = [],
  { startDate, endDate } = {},
) => {
  const startKey = parseDayKey(startDate);
  const endKey = parseDayKey(endDate) || startKey;

  if (!startKey || !endKey) {
    return { labels: [], entrada: [], saida: [], days: [] };
  }

  const start = parseISO(startKey);
  const end = parseISO(endKey);
  const dayCount = Math.max(0, differenceInCalendarDays(end, start)) + 1;
  const cappedDays = Math.min(dayCount, 31);

  const byDay = new Map();
  for (let i = 0; i < cappedDays; i += 1) {
    const key = format(addDays(start, i), "yyyy-MM-dd");
    byDay.set(key, { entradaCents: 0, saidaCents: 0 });
  }

  (items || []).forEach((item) => {
    if (!isPixStatementItem(item)) return;
    const dayKey = parseDayKey(item?.created_at);
    if (!dayKey || !byDay.has(dayKey)) return;

    const bucket = byDay.get(dayKey);
    addPixInOutCents(item, bucket);
  });

  const days = [...byDay.entries()].map(([key, values]) => ({
    key,
    label: format(parseISO(key), "dd/MM"),
    ...values,
  }));

  return {
    labels: days.map((day) => day.label),
    entrada: days.map((day) => day.entradaCents / 100),
    saida: days.map((day) => day.saidaCents / 100),
    days,
  };
};
